import { t } from "@lingui/macro";
import type { Call } from "@repo/common";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { useState } from "react";
import { getContentFieldProps } from "../utils/textDirection";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";

type TranscriptFeedbackValue = NonNullable<Call["transcriptFeedback"]>;

interface TranscriptFeedbackProps {
	value?: TranscriptFeedbackValue | null;
	disabled?: boolean;
	language?: "en" | "he";
	onSubmit: (
		rating: TranscriptFeedbackValue["rating"],
		comment?: string,
	) => Promise<void>;
}

export function TranscriptFeedback({
	value,
	disabled = false,
	language = "en",
	onSubmit,
}: TranscriptFeedbackProps) {
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [comment, setComment] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	const submitRating = async (
		rating: TranscriptFeedbackValue["rating"],
		feedbackComment?: string,
	) => {
		setIsSaving(true);
		try {
			await onSubmit(rating, feedbackComment);
		} finally {
			setIsSaving(false);
		}
	};

	const handlePositive = () => {
		void submitRating("positive").catch(() => {
			// The parent reports persistence errors.
		});
	};

	const handleNegative = () => {
		setComment(value?.rating === "negative" ? value.comment || "" : "");
		setIsDialogOpen(true);
		if (value?.rating !== "negative") {
			void submitRating("negative").catch(() => {
				// The parent reports persistence errors and the dialog stays open.
			});
		}
	};

	const handleFeedbackSubmit = async () => {
		const trimmedComment = comment.trim();
		if (!trimmedComment) return;
		try {
			await submitRating("negative", trimmedComment);
			setIsDialogOpen(false);
		} catch {
			// The parent reports persistence errors and the dialog stays open for retry.
		}
	};

	return (
		<>
			<div className="flex items-center gap-1.5 text-xs text-muted-foreground">
				<span className="me-1">
					{t({
						id: "transcriptFeedback.prompt",
						message: "Was this transcript accurate?",
					})}
				</span>
				<Button
					variant="ghost"
					size="icon"
					className={`h-7 w-7 ${
						value?.rating === "positive"
							? "bg-live-tint text-foreground hover:bg-live-tint hover:text-foreground"
							: "text-muted-foreground hover:text-foreground"
					}`}
					onClick={handlePositive}
					disabled={disabled || isSaving}
					aria-pressed={value?.rating === "positive"}
					aria-label={t({
						id: "transcriptFeedback.positive",
						message: "Transcript was accurate",
					})}
				>
					<ThumbsUp className="h-3.5 w-3.5" />
				</Button>
				<Button
					variant="ghost"
					size="icon"
					className={`h-7 w-7 ${
						value?.rating === "negative"
							? "bg-danger-tint text-danger-ink hover:bg-danger-tint hover:text-danger-ink"
							: "text-muted-foreground hover:text-danger-ink"
					}`}
					onClick={handleNegative}
					disabled={disabled || isSaving}
					aria-pressed={value?.rating === "negative"}
					aria-label={t({
						id: "transcriptFeedback.negative",
						message: "Transcript had issues",
					})}
				>
					<ThumbsDown className="h-3.5 w-3.5" />
				</Button>
			</div>

			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="sm:max-w-[460px]">
					<DialogHeader>
						<DialogTitle>
							{t({
								id: "transcriptFeedback.dialogTitle",
								message: "Tell us what went wrong",
							})}
						</DialogTitle>
						<DialogDescription>
							{t({
								id: "transcriptFeedback.dialogDescription",
								message:
									"Your feedback helps us improve transcript accuracy. Adding details is optional.",
							})}
						</DialogDescription>
					</DialogHeader>
					<Textarea
						value={comment}
						onChange={(event) => setComment(event.target.value)}
						placeholder={t({
							id: "transcriptFeedback.placeholder",
							message: "For example, a speaker or phrase was incorrect…",
						})}
						maxLength={1000}
						rows={5}
						{...getContentFieldProps(
							comment,
							language === "he" ? "rtl" : "ltr",
						)}
					/>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsDialogOpen(false)}
							disabled={isSaving}
						>
							{t({
								id: "transcriptFeedback.notNow",
								message: "Not now",
							})}
						</Button>
						<Button
							onClick={() => void handleFeedbackSubmit()}
							disabled={isSaving || !comment.trim()}
						>
							{t({
								id: "transcriptFeedback.send",
								message: "Send feedback",
							})}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
